import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { StatusIcon } from './StatusIcon';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { topicBySlug, topicPath } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import type { ProblemSummary } from '@/types';

export function ProblemTable({ problems }: { problems: ProblemSummary[] }) {
  const { t } = useTranslation();
  const { l } = useLocalized();

  if (problems.length === 0) {
    return (
      <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
        {t('problems.empty')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-elevated">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-muted">
            <th className="w-12 px-4 py-3 text-center">{t('problems.colStatus')}</th>
            <th className="w-16 px-2 py-3">{t('problems.colId')}</th>
            <th className="px-2 py-3">{t('problems.colTitle')}</th>
            <th className="hidden px-2 py-3 md:table-cell">{t('problems.colDifficulty')}</th>
            <th className="hidden px-2 py-3 text-right sm:table-cell">{t('problems.colAcceptance')}</th>
            <th className="hidden px-4 py-3 lg:table-cell">{t('problems.colTopics')}</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((p) => (
            <tr key={p.id} className="group border-b border-line last:border-0 transition-colors hover:bg-surface">
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center">
                  <StatusIcon status={p.status} />
                </div>
              </td>
              <td className="px-2 py-3 font-mono text-xs text-content-muted">{p.id}</td>
              <td className="px-2 py-3">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/problems/${p.slug}`}
                    className="font-medium text-content transition-colors group-hover:text-brand-600"
                  >
                    {l(p.title)}
                  </Link>
                  {p.isPremium && (
                    <span className="inline-flex items-center gap-1 rounded bg-[var(--medium)]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--medium)]">
                      <Lock className="h-3 w-3" />
                      {t('problems.premium')}
                    </span>
                  )}
                </div>
                <div className="mt-1 md:hidden">
                  <DifficultyBadge difficulty={p.difficulty} />
                </div>
              </td>
              <td className="hidden px-2 py-3 md:table-cell">
                <DifficultyBadge difficulty={p.difficulty} />
              </td>
              <td className="hidden px-2 py-3 text-right font-mono text-xs text-content-secondary sm:table-cell">
                {p.acceptance === null ? '—' : `${p.acceptance}%`}
              </td>
              <td className="hidden px-4 py-3 lg:table-cell">
                <div className="flex flex-wrap gap-1">
                  {p.topics.map((slug) => {
                    const tp = topicBySlug(slug);
                    return tp ? (
                      <Link key={slug} to={topicPath(tp.slug)} className="tag transition-colors hover:text-brand-600">
                        {l(tp.title)}
                      </Link>
                    ) : null;
                  })}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
